/**
 * Cohort picker card — the right-hand card of Orientation Assignment
 * when the target is a cohort instead of individual users. Radio-select
 * over the site's non-archived rosters.
 *
 * A cohort that already holds a non-cancelled pairing with the selected
 * orientation renders disabled: the module enforces one live pairing per
 * (cohort, orientation), so offering it would only harvest a refusal.
 */
import type { CohortEntityRow } from '@/hooks/useCohortEntities';
import { GatedButton } from '../GatedButton';

export function CohortTargetCard({
  cohorts,
  isReady,
  selectedOrientationId,
  selectedCohortId,
  onSelect,
}: {
  /** Non-archived cohorts of the current site, newest first. */
  cohorts: CohortEntityRow[];
  isReady: boolean;
  /** Currently picked orientation, for the already-paired lockout. */
  selectedOrientationId: string | null;
  selectedCohortId: bigint | null;
  onSelect: (cohortId: bigint) => void;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Pick cohort</h3>
          <div className="sub">
            The whole roster gets the orientation in one act — one
            assignment per member, collisions adopted or skipped with
            reasons on the Cohorts tab.
          </div>
        </div>
      </div>
      {!isReady ? (
        <CardNote>Loading cohorts…</CardNote>
      ) : cohorts.length === 0 ? (
        <CardNote>
          No cohorts on this site yet — create one on the <b>Cohorts</b> tab
          first.
        </CardNote>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {cohorts.map((c) => {
            const alreadyPaired =
              selectedOrientationId != null &&
              c.pairings.some(
                (p) =>
                  p.orientationId === selectedOrientationId &&
                  p.storedStatus === 'Active',
              );
            const selected = selectedCohortId === c.cohortId;
            return (
              <GatedButton
                key={String(c.cohortId)}
                className="nav-item"
                reason={
                  alreadyPaired
                    ? 'This cohort already has this orientation — cancel that pairing on the Cohorts tab to re-assign.'
                    : null
                }
                onClick={() => onSelect(c.cohortId)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 14px',
                  borderRadius: 0,
                  borderTop: '1px solid var(--border-soft, var(--border))',
                  background: selected
                    ? 'var(--amber-soft, var(--panel-2))'
                    : 'transparent',
                  borderLeft: selected
                    ? '2px solid var(--amber, var(--good))'
                    : '2px solid transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: alreadyPaired ? 'not-allowed' : 'pointer',
                  opacity: alreadyPaired ? 0.55 : 1,
                }}
              >
                <RadioDot on={selected} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13 }}>
                    <span
                      style={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c.name}
                    </span>
                    {alreadyPaired ? (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--muted)',
                          background: 'var(--panel-3)',
                          padding: '1px 6px',
                          borderRadius: 8,
                          textTransform: 'uppercase',
                          letterSpacing: '.04em',
                          flexShrink: 0,
                        }}
                      >
                        Already assigned
                      </span>
                    ) : null}
                  </span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    {c.members.length} member{c.members.length === 1 ? '' : 's'}
                    {c.pairings.length > 0
                      ? ` · ${c.pairings.length} orientation${c.pairings.length === 1 ? '' : 's'} so far`
                      : ''}
                    {' · by '}
                    {c.createdByEmail || 'unknown'}
                  </span>
                </span>
              </GatedButton>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CardNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 24,
        textAlign: 'center',
        color: 'var(--muted)',
        fontSize: 12.5,
      }}
    >
      {children}
    </div>
  );
}

function RadioDot({ on }: { on: boolean }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        border: '2px solid var(--border-2, var(--border))',
        background: on ? 'var(--amber, var(--good))' : 'transparent',
        marginTop: 3,
        flexShrink: 0,
      }}
    />
  );
}
